import type { QuestionDefinition } from "@reno-estimator/core";
import { ADDITION_SIZE_RANGES } from "../addition-size-ranges.js";

/**
 * The Home Addition Estimator (Phase 12). Per the brief, an addition is
 * treated as a HIGH-COMPLEXITY project type: it can touch nearly every
 * system in the house (foundation, structure, HVAC, plumbing,
 * electrical, envelope, roofing) at once, and - unlike a kitchen or
 * basement remodel confined to an existing room - it also has to
 * integrate with the existing home's structure, roofline, and site.
 * That integration is exactly what a self-reported questionnaire cannot
 * fully capture, which is why the calculator (see
 * `packages/core/src/calculation/rules/addition.ts`) always attaches a
 * standing "site conditions/structural integration vary" disclosure
 * regardless of which answers are given here - see
 * docs/addition-estimator.md.
 *
 * Question set, following the same tiered/gated pattern kitchen (Phase
 * 5A) and basement (Phase 8) established:
 *  - Core scoping: size (bucketed, own dedicated Step 2 UI like kitchen/
 *    basement), storeys, and foundation requirements - all three
 *    materially change the base construction cost and are asked before
 *    anything else.
 *  - Room composition: kitchen scope, bathroom scope, and a bedroom
 *    count, each priced as its own discrete line item(s) rather than
 *    folded into a single sqft multiplier - an addition with a new
 *    kitchen and two bathrooms costs very differently from a bare shell
 *    of the same footprint.
 *  - Structural complexity as its own explicit tier (rather than only
 *    the common `structural_changes` yes/no/unknown, which asks about
 *    the EXISTING space) - answers how much the addition's structure
 *    must integrate with the existing home's roof/walls/foundation,
 *    which is the single biggest cost/risk driver the brief calls out.
 *  - Systems: HVAC, electrical, and plumbing each get their own scope
 *    tier question, since "does this need plumbing" is close to always
 *    "yes" for an addition (unlike, say, a living room) and a flat
 *    yes/no from the common questions wouldn't distinguish extending an
 *    existing line from a full new supply.
 *  - Envelope/exterior: windows/doors tier, roofing approach, and
 *    exterior finish tier - each independently priced since a
 *    match-existing-brick exterior and a builder-grade vinyl exterior
 *    are not the same line item at different multipliers.
 *  - Design/architectural services and engineering are asked as two
 *    separate questions (not the single common `needs_design_services`
 *    boolean) because the brief lists them as distinct addition
 *    categories, and in practice they're usually both needed and priced
 *    separately (a drafter/designer vs. a stamped structural engineering
 *    package) - see rules/addition.ts.
 *  - Permit requirements and finish level reuse the existing common
 *    `permit_likely_required`/`finish_level` questions; the calculator
 *    adds an addition-specific warning if permits are declined outright,
 *    since a physical addition to a home requires a building permit in
 *    essentially every Canadian jurisdiction.
 */

const ADDITION_SIZE_RANGE_OPTIONS = ADDITION_SIZE_RANGES.map((r) => ({ id: r.id, label: r.label }));

export const additionQuestions: QuestionDefinition[] = [
  {
    id: "addition_size_range",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "How large is the addition?",
    helpText: "A rough range is fine - you can enter an exact square footage instead if you know it.",
    required: false,
    options: ADDITION_SIZE_RANGE_OPTIONS,
    order: 15
  },
  {
    id: "addition_storeys",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "Is this a single-storey or multi-storey addition?",
    helpText: "A second-storey addition (built above existing structure) has different structural requirements than one built at ground level.",
    required: true,
    options: [
      { id: "single_storey", label: "Single storey", description: "One level, built at ground level." },
      { id: "two_storey", label: "Two storeys", description: "A new two-storey section." },
      { id: "second_storey_addition", label: "Second storey over existing structure", description: "Adding a new storey on top of an existing single-storey area - requires assessing the existing structure's load capacity." }
    ],
    order: 190
  },
  {
    id: "addition_foundation_type",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "What foundation does the addition need?",
    helpText: "If you're not sure, choose \"Not sure yet\" - foundation type is one of the biggest cost drivers and should be confirmed with a contractor/engineer before finalizing budget.",
    required: true,
    options: [
      { id: "none_existing_structure", label: "None - built over existing structure", description: "E.g. a second-storey addition with no new foundation needed." },
      { id: "slab_on_grade", label: "Slab on grade", description: "A concrete slab poured at ground level." },
      { id: "crawl_space", label: "Crawl space", description: "A shallow foundation with access space beneath the addition." },
      { id: "full_foundation_basement", label: "Full foundation / basement", description: "A full-depth poured foundation, extending usable space below the addition." },
      { id: "unsure", label: "Not sure yet", description: "Foundation type has not been determined." }
    ],
    order: 200
  },
  {
    id: "addition_kitchen_scope",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "Will the addition include a kitchen?",
    required: false,
    options: [
      { id: "none", label: "No kitchen" },
      { id: "renovate_existing", label: "Extending/renovating the adjacent existing kitchen" },
      { id: "new_kitchen", label: "A brand-new kitchen" }
    ],
    order: 210
  },
  {
    id: "addition_bathroom_scope",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "Will the addition include a bathroom?",
    required: false,
    options: [
      { id: "none", label: "No bathroom" },
      { id: "one_bathroom", label: "One bathroom" },
      { id: "multiple_bathrooms", label: "Two or more bathrooms" }
    ],
    order: 220
  },
  {
    id: "addition_bedrooms",
    projectTypes: ["addition"],
    inputType: "number",
    prompt: "How many bedrooms will the addition include?",
    helpText: "Enter 0 if the addition won't include a bedroom.",
    required: false,
    min: 0,
    max: 10,
    order: 230
  },
  {
    id: "addition_structural_complexity",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "How complex is tying the addition into the existing home's structure?",
    helpText: "This is one of the biggest cost and risk factors for an addition, and is genuinely hard to assess without seeing the house - if unsure, choose a higher complexity tier and confirm with a contractor/engineer.",
    required: true,
    options: [
      { id: "standard", label: "Standard", description: "A simple ground-level box addition with a straightforward roof/wall tie-in." },
      { id: "moderate", label: "Moderate", description: "Some roofline matching, a load-bearing wall opening, or a modest structural tie-in." },
      { id: "complex", label: "Complex", description: "Major structural changes - e.g. removing exterior load-bearing walls, cantilevers, matching a complex roofline, or building over existing structure." }
    ],
    order: 240
  },
  {
    id: "addition_hvac_scope",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "What's needed for heating/cooling the addition?",
    required: false,
    options: [
      { id: "extend_existing", label: "Extend existing system", description: "Add ductwork/registers or a supplemental unit off the existing furnace/AC." },
      { id: "dedicated_system", label: "Dedicated zone/system", description: "A separate zone, mini-split, or standalone system for the addition." },
      { id: "unsure", label: "Not sure yet" }
    ],
    order: 250
  },
  {
    id: "addition_electrical_scope",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "What's the electrical scope for the addition?",
    required: false,
    options: [
      { id: "standard_wiring", label: "Standard new wiring", description: "New circuits off the existing panel - no panel upgrade expected." },
      { id: "panel_upgrade_required", label: "Panel upgrade likely required", description: "The existing electrical panel may not have capacity for the addition." },
      { id: "not_sure", label: "Not sure yet" }
    ],
    order: 260
  },
  {
    id: "addition_plumbing_scope",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "What's the plumbing scope for the addition?",
    required: false,
    options: [
      { id: "none_no_plumbing", label: "None needed", description: "No bathroom, kitchen, or wet bar in the addition." },
      { id: "extend_existing_lines", label: "Extend existing lines", description: "Tie into nearby existing supply/drain lines." },
      { id: "new_dedicated_lines", label: "New dedicated lines", description: "New supply/drain runs, possibly including a new stack." }
    ],
    order: 270
  },
  {
    id: "addition_windows_doors_scope",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "What level of windows and doors are you planning?",
    required: false,
    options: [
      { id: "standard", label: "Standard", description: "Builder-grade vinyl windows and doors." },
      { id: "upgraded", label: "Upgraded", description: "Higher-efficiency windows, larger openings, or upgraded exterior doors." },
      { id: "premium", label: "Premium", description: "Large-format/specialty windows, sliding or folding glass doors, premium hardware." }
    ],
    order: 280
  },
  {
    id: "addition_roofing_type",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "What's the roofing approach for the addition?",
    required: false,
    options: [
      { id: "tie_into_existing_roofline", label: "Tie into the existing roofline", description: "Matching and extending the current roof." },
      { id: "new_roof_section", label: "New, separate roof section", description: "A distinct roof section (e.g. a different pitch or style)." },
      { id: "flat_roof", label: "Flat / low-slope roof" }
    ],
    order: 290
  },
  {
    id: "addition_exterior_finish",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "What exterior finish are you planning?",
    helpText: "Matching an existing brick or stone exterior is typically the most expensive option, since it usually requires sourcing a close match.",
    required: false,
    options: [
      { id: "match_existing", label: "Match the existing exterior", description: "Sourced/finished to match the current siding, brick, or stone." },
      { id: "vinyl_siding", label: "Vinyl siding" },
      { id: "brick_stone", label: "New brick or stone" },
      { id: "premium_finish", label: "Premium finish", description: "E.g. fibre cement panel systems, natural wood siding, or a mixed high-end exterior." }
    ],
    order: 300
  },
  {
    id: "addition_design_services",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "Do you need design/architectural drawings for the addition?",
    helpText: "Most municipalities require drawings for a building permit application, separate from any structural engineering.",
    required: true,
    options: [
      { id: "yes", label: "Yes" },
      { id: "no", label: "No - already have drawings" },
      { id: "unknown", label: "Not sure" }
    ],
    order: 310
  },
  {
    id: "addition_engineering_required",
    projectTypes: ["addition"],
    inputType: "single_select",
    prompt: "Will structural engineering be required?",
    helpText: "Almost all additions require at least some engineered drawings (e.g. for footings or a structural tie-in) - if unsure, choose \"Not sure\" rather than \"No\".",
    required: true,
    options: [
      { id: "yes", label: "Yes" },
      { id: "no", label: "No" },
      { id: "unknown", label: "Not sure" }
    ],
    order: 320
  }
];
